import React from "react";

function shouldShowImmediately() {
  if (typeof window === "undefined") return true;
  if (typeof window.IntersectionObserver !== "function") return true;
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

const hiddenVariantClasses = {
  rise: "translate-y-10 scale-[.99]",
  left: "-translate-x-8 scale-[.99]",
  right: "translate-x-8 scale-[.99]",
  zoom: "translate-y-4 scale-[.97]",
};

export default function Reveal({ children, className = "", delay = 0, replay = false, variant = "rise" }) {
  const elementRef = React.useRef(null);
  const [isVisible, setIsVisible] = React.useState(shouldShowImmediately);

  React.useEffect(() => {
    if (shouldShowImmediately()) {
      setIsVisible(true);
      return undefined;
    }

    const element = elementRef.current;
    if (!element) return undefined;

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (!replay) observer.disconnect();
          return;
        }

        if (replay) setIsVisible(false);
      },
      { rootMargin: "4% 0px -12% 0px", threshold: 0.08 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [replay]);

  const hiddenClasses = hiddenVariantClasses[variant] ?? hiddenVariantClasses.rise;

  return (
    <div
      ref={elementRef}
      data-reveal="section"
      data-visible={isVisible}
      data-replay={replay}
      data-reveal-variant={variant}
      style={{ transitionDelay: isVisible ? `${delay}ms` : "0ms" }}
      className={`transform-gpu transition-[opacity,transform] duration-[900ms] ease-out motion-reduce:transform-none motion-reduce:opacity-100 motion-reduce:transition-none ${
        isVisible ? "translate-x-0 translate-y-0 scale-100 opacity-100" : `${hiddenClasses} opacity-0`
      } ${className}`}
    >
      {children}
    </div>
  );
}
